import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import SinConexionKawaii from "../SinConexionKawaii";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import { getGuideAccent } from "../../utils/guideAccent";

// Template312 (Edimburgo, copia de 112): Día 1 - Mapa Borough Market y Southbank
// Adaptado del componente Page5.jsx de guia-londres para usar las props del sistema guia
const Template312 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const mapFullscreenContext = useMapFullscreen();
	const setMapFullscreen = mapFullscreenContext?.setMapFullscreen;

	const openFullscreen = () => {
		setFullscreen(true);
		if (typeof setMapFullscreen === "function") setMapFullscreen(true);
	};
	const closeFullscreen = () => {
		setFullscreen(false);
		if (typeof setMapFullscreen === "function") setMapFullscreen(false);
	};

	useEffect(() => {
		return () => {
			if (typeof setMapFullscreen === "function") setMapFullscreen(false);
		};
	}, [setMapFullscreen]);

	useEffect(() => {
		if (typeof navigator !== "undefined") {
			setIsOnline(navigator.onLine);
			setLoaded(navigator.onLine);
		}

		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};

		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);

		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const skylineImage = getCanvasImageUrl("skyline");
	const mapaOffline = getImageSrc("mapa-offline");
	const boroughImage = getImageSrc("borough");
	const southbankSticker = getCanvasImageUrl("southbank");

	const mapUrl = page.maps?.[0];
	const introText = page.texts?.[1]?.content;
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const ui = page?.ui ?? page?.data?.ui ?? page?.data?.data?.ui ?? {};

	let actualHeaderColor = headerBackgroundColor || "#de5624";
	const accent = getGuideAccent(actualHeaderColor);

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	if (fullscreen) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: actualHeaderColor,
					}}
				>
					<Box
						component="button"
						onClick={closeFullscreen}
						sx={{
							backgroundColor: "#fff",
							color: actualHeaderColor,
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						}}
					>
						{ui.backButton || "← Volver a la guía"}
					</Box>
				</Box>
				<iframe
					title={ui.mapTitleFullscreen || "Mapa grande"}
					src={mapUrl}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				></iframe>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					zIndex: 2,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* MAPA: minHeight + contenedor con altura definida (position absolute inset) para que el iframe height:100% resuelva igual en Viewer y StickerApp */}
			<Box
				data-block-id="map"
				sx={{
					flex: 1,
					minHeight: 320,
					marginTop: "-70px",
					position: "relative",
				}}
			>
				{/* Contenedor que llena el padre: así el iframe (height 100%) tiene siempre una altura de referencia */}
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
					}}
				>
					{!loaded && isOnline && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								zIndex: 1,
							}}
						>
							<Box
								sx={{
									width: 20,
									height: 20,
									border: "4px solid #ccc",
									borderTop: `4px solid ${actualHeaderColor}`,
									borderRadius: "50%",
									animation: "spin 1s linear infinite",
									"@keyframes spin": {
										"0%": { transform: "rotate(0deg)" },
										"100%": { transform: "rotate(360deg)" },
									},
								}}
							/>
						</Box>
					)}

					{isOnline && (
						<Box
							component="button"
							onClick={openFullscreen}
							sx={{
								position: "absolute",
								top: 80,
								right: 10,
								backgroundColor: actualHeaderColor,
								color: "#fff!important",
								padding: "8px 10px",
								fontSize: "11px",
								borderRadius: "8px",
								textDecoration: "none",
								fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
								boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
								border: "none",
								cursor: "pointer",
							}}
						>
							{ui.fullscreenButton || "Ver mapa grande"}
						</Box>
					)}

					{isOnline && mapUrl ? (
						<iframe
							title={ui.mapTitleEmbed || "Mapa Londres"}
							src={mapUrl}
							width="100%"
							height="100%"
							style={{ border: 0, display: "block" }}
							onLoad={() => setLoaded(true)}
						></iframe>
					) : mapaOffline ? (
						<img
							src={mapaOffline}
							alt=""
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
								position: "absolute",
								top: 0,
								left: 0,
							}}
						/>
					) : !isOnline ? (
						<SinConexionKawaii height="100%" fontFamily={fontFamily} />
					) : null}
				</Box>

				{/* Tooltip Shard */}
				<Box
					sx={{
						position: "absolute",
						top: 80,
						left: 10,
						backgroundColor: accent.tooltipBg,
						border: "2px solid #f5c400",
						borderRadius: "16px",
						padding: "10px 12px",
						fontSize: 11,
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						color: contentColor,
						boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
						maxWidth: 160,
						zIndex: 3,
					}}
				>
					{!isSmallScreen && (
						<img
							src={skylineImage}
							alt="londres"
							style={{ width: 60, zIndex: 200 }}
						/>
					)}
					<Box>
						{ui.tooltipShard?.title && <strong>{ui.tooltipShard.title}</strong>}
						{ui.tooltipShard?.body && (
							<>
								<br />
								{ui.tooltipShard.body}
							</>
						)}
					</Box>
				</Box>

				<img
					src={southbankSticker}
					alt="southbank"
					style={{
						width: isSmallScreen ? 80 : 110,
						position: "absolute",
						bottom: -10,
						left: -18,
					}}
				/>

				<Box
					sx={{
						position: "absolute",
						bottom: isSmallScreen ? 20 : 60,
						left: 50,
						backgroundColor: accent.tooltipBg,
						border: "2px solid #f5c400",
						borderRadius: "16px 16px 16px 0px",
						padding: "10px 12px",
						fontSize: 11,
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						color: contentColor,
						boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
						maxWidth: isSmallScreen ? 120 : 200,
					}}
				>
					{ui.tooltipSouthbank?.title && (
						<strong>{ui.tooltipSouthbank.title}</strong>
					)}
					{ui.tooltipSouthbank && (
						<>
							<br />
							{isSmallScreen
								? ui.tooltipSouthbank.bodyShort || ""
								: ui.tooltipSouthbank.bodyLong || ""}
						</>
					)}
				</Box>
			</Box>

			{/* EXPLICACIÓN */}
			{introText && (
				<Box
					data-block-id="text-1"
					sx={{
						padding: "12px 16px",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: 12,
						backgroundColor: "#fff",
						color: "#333",
					}}
				>
					{introText}
				</Box>
			)}

			{sections.map((section, idx) => {
				const imageSrc = getImageSrc(section.imageId);
				return (
					<Box
						key={idx}
						data-block-id={`text-${idx + 2}`}
						sx={{
							display: "flex",
							gap: "16px",
							alignItems: "center",
							padding: "12px 16px",
							paddingTop: introText ? 0 : 12,
							backgroundColor: "#ffffff",
							position: "relative",
						}}
					>
						{imageSrc && (
							<img
								src={imageSrc}
								alt={section.title}
								style={{
									width: "40%",
									height: 110,
									borderRadius: 12,
									objectFit: "cover",
								}}
							/>
						)}
						<Box sx={{ width: "60%" }}>
							{section.title && (
								<Box component="h3" sx={h3}>
									{section.title}
								</Box>
							)}
							{section.body && (
								<Box component="p" sx={p}>
									{section.body}
								</Box>
							)}
						</Box>
						{section.sabiasQue?.map((sq, i) => (
							<Box
								key={i}
								sx={{
									position: "absolute",
									top: isSmallScreen ? 20 : -10,
									left: "35%",
								}}
							>
								<SabiasQue icono={sq.icono} texto={sq.texto} />
							</Box>
						))}
					</Box>
				);
			})}
		</Box>
	);
};

Template312.thumbnail = "/images/template-1.png";

export default Template312;
